<template>
  <div class="agent-upload-page">
    <div class="agent-upload-inner">
      <header class="page-header">
        <p class="eyebrow">{{ requesterLabel }}</p>
        <h1>{{ pageTitle }}</h1>
        <p class="purpose-lead">{{ purposeLead }}</p>
        <p v-if="checkName" class="page-meta">
          Reviewed against {{ checkName }}
          <template v-if="locationLabel"> · {{ locationLabel }}</template>
        </p>
        <p v-else-if="locationLabel" class="page-meta">{{ locationLabel }}</p>
      </header>

      <section
        v-if="headline || isGapRequest"
        class="purpose-panel"
        :class="{ 'purpose-panel--alert': isGapRequest }"
      >
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-clipboard-list"></i>
        </div>
        <div class="purpose-body">
          <h2>{{ isGapRequest ? 'What needs attention' : 'Why you are here' }}</h2>
          <p v-if="headline" class="headline">{{ headline }}</p>
          <p class="next-action">
            <span class="next-label">Next:</span> {{ nextAction }}
          </p>
          <pre v-if="gapSummary" class="gap-summary">{{ gapSummary }}</pre>
        </div>
      </section>

      <section v-if="certificateHolder" class="meta-panel">
        <div class="meta-row">
          <div class="meta-label">Certificate holder</div>
          <div class="meta-value">
            {{ certificateHolder.name }}
            <div v-if="certificateHolder.address" class="meta-sub">
              {{ certificateHolder.address }}
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="substantiveCustomSummary.length"
        class="purpose-panel"
      >
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-info-circle"></i>
        </div>
        <div class="purpose-body">
          <h2>From the requesting team</h2>
          <p
            v-for="(paragraph, idx) in substantiveCustomSummary"
            :key="'custom-' + idx"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <section
        v-if="sampleDocuments && sampleDocuments.length > 0"
        class="purpose-panel samples-panel"
      >
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-file-download"></i>
        </div>
        <div class="purpose-body">
          <h2>Sample documents</h2>
          <p>Optional reference for expected format before you upload.</p>
          <ul class="sample-list">
            <li v-for="sampleDoc in sampleDocuments" :key="sampleDoc.id" class="sample-item">
              <div class="sample-item-details">
                <div class="sample-item-name">{{ sampleDoc.name }}</div>
                <div v-if="sampleDoc.description" class="sample-description">
                  {{ sampleDoc.description }}
                </div>
              </div>
              <a
                :href="getFullDownloadUrl(sampleDoc.download_url)"
                class="sample-download"
                target="_blank"
              >
                Download
              </a>
            </li>
          </ul>
        </div>
      </section>

      <div v-if="isBulkMode" class="upload-section">
        <SecureUploadBulkPanel
          :token="token"
          :project="project"
          :check-name="checkName"
          :insured-label="insuredLabel"
        />
      </div>

      <section
        v-else
        class="upload-drop"
        :class="{ 'is-drag-over': isDragOver }"
        @drop.prevent="handleDrop"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @click="openFilePicker"
        role="button"
        tabindex="0"
        @keydown.enter.prevent="openFilePicker"
        @keydown.space.prevent="openFilePicker"
        :aria-label="uploadAriaLabel"
      >
        <input
          ref="fileInput"
          type="file"
          class="home-upload-input"
          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
          @change="handleFileSelect"
          @click.stop
        >
        <div class="upload-drop-inner">
          <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
          <h2>{{ selectedFile ? selectedFile.name : 'Upload a certificate' }}</h2>
          <p class="upload-purpose">
            Drop a COI or supporting document to review against
            <strong>{{ checkName || 'this project’s insurance requirements' }}</strong>
            for <strong>{{ insuredLabel }}</strong>.
          </p>
          <p v-if="!selectedFile" class="upload-hint">
            or click to browse · PDF, DOC, DOCX (max 50MB)
          </p>
          <button
            v-else
            type="button"
            class="remove-file"
            @click.stop="removeFile"
          >
            Remove file
          </button>
        </div>
      </section>

      <form v-if="!isBulkMode && !error" class="upload-actions" @submit.prevent="uploadDocument">
        <button
          type="submit"
          class="btn-primary"
          :disabled="!selectedFile || isUploading"
        >
          {{ submitLabel }}
        </button>
        <div v-if="isUploading" class="upload-progress">
          <div class="upload-progress-bar">
            <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p>
            {{ uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Queuing review…' }}
          </p>
        </div>
        <p v-if="uploadError" class="error-text">{{ uploadError }}</p>
      </form>

      <div v-if="error" class="error-panel">
        <h2>Upload link issue</h2>
        <p>{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import SecureUploadBulkPanel from '@/components/SecureUploadBulkPanel.vue';

export default {
  name: 'SecureUploadLanding',
  components: { SecureUploadBulkPanel },
  data() {
    return {
      token: null,
      project: null,
      sampleDocuments: [],
      checkDefinition: null,
      complianceSubject: null,
      uploadRequest: null,
      requestContext: null,

      selectedFile: null,
      isUploading: false,
      uploadProgress: 0,
      isDragOver: false,
      uploadError: null,
      error: null
    };
  },

  computed: {
    isBulkMode() {
      return Boolean(this.uploadRequest && this.uploadRequest.skip_processing);
    },

    requesterLabel() {
      const raw =
        this.requestContext?.requester_name ||
        this.project?.organization_name ||
        this.project?.name ||
        this.checkDefinition?.name ||
        'the requesting organization';
      return String(raw)
        .replace(/\s+Compliance Program$/i, '')
        .replace(/\s+Insurance Requirements$/i, '')
        .trim() || 'the requesting organization';
    },

    insuredLabel() {
      return this.complianceSubject?.entity_name || 'your insured';
    },

    checkName() {
      return (
        this.requestContext?.check_name ||
        this.checkDefinition?.name ||
        this.project?.default_comprehensive_check_name ||
        ''
      );
    },

    locationLabel() {
      return this.complianceSubject?.location?.name || '';
    },

    certificateHolder() {
      const holder = this.requestContext?.certificate_holder;
      if (holder && (holder.name || holder.address)) return holder;
      return null;
    },

    isGapRequest() {
      return Boolean(this.requestContext?.is_gap);
    },

    gapSummary() {
      return (this.requestContext?.gap_summary || '').trim();
    },

    headline() {
      return (this.requestContext?.headline || '').trim();
    },

    nextAction() {
      return (
        this.requestContext?.next_action ||
        'Upload a current certificate of insurance (or related endorsements).'
      );
    },

    pageTitle() {
      if (this.isBulkMode) return 'Upload certificates';
      if (this.isGapRequest) return 'Upload an updated certificate';
      return 'Upload a certificate';
    },

    purposeLead() {
      if (this.isBulkMode) {
        return `${this.requesterLabel} asked you to collect certificates for ${this.insuredLabel}. The requesting team will review them against this project’s requirements before processing.`;
      }
      return `${this.requesterLabel} asked you to upload a certificate of insurance (or related endorsements) for ${this.insuredLabel} so they can review it against this project’s requirements.`;
    },

    uploadAriaLabel() {
      return `Upload a certificate of insurance for ${this.insuredLabel}`;
    },

    submitLabel() {
      if (this.isUploading) {
        return this.uploadProgress < 100
          ? `Uploading (${this.uploadProgress}%)…`
          : 'Queuing review…';
      }
      return this.isGapRequest ? 'Upload updated certificate' : 'Upload certificate';
    },

    substantiveCustomSummary() {
      const candidates = [
        this.checkDefinition?.user_summary,
        this.checkDefinition?.description,
      ].filter(Boolean);

      for (const text of candidates) {
        if (this.isSubstantiveSummary(text)) {
          return this.formatSummaryParagraphs(text);
        }
      }
      return [];
    },
  },

  async created() {
    this.token = this.$route.params.token;
    await this.fetchUploadDetails();
  },

  methods: {
    async fetchUploadDetails() {
      try {
        const response = await api.get(`/secure-upload/${this.token}/`);
        const data = response.data;

        if (data.status === 'success') {
          this.project = data.project;
          this.sampleDocuments = data.sample_documents || [];
          this.checkDefinition = data.check_definition || null;
          this.complianceSubject = data.compliance_subject || null;
          this.uploadRequest = data.upload_request || null;
          this.requestContext = data.request_context || null;
        } else {
          this.error = data.message || 'Failed to load upload details';
        }
      } catch (err) {
        this.error = 'Failed to load upload page. Please check your link.';
      }
    },

    openFilePicker() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.uploadError = null;
      }
    },

    handleDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
        this.uploadError = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    async uploadDocument() {
      if (!this.selectedFile) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadError = null;

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const response = await api.post(`/secure-upload/${this.token}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });

        if (response.data.status === 'success') {
          this.$router.push({
            name: 'SecureUploadAnalysis',
            params: { token: this.token },
            query: { documentId: response.data.document.id }
          });
        } else {
          this.uploadError = response.data.message || 'Upload failed';
        }
      } catch (err) {
        this.uploadError = err.response?.data?.message || 'Failed to upload document. Please try again.';
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },

    getFullDownloadUrl(downloadUrl) {
      if (!downloadUrl) return '';
      if (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')) {
        return downloadUrl;
      }
      return window.location.origin + downloadUrl;
    },

    formatSummaryParagraphs(summary) {
      if (!summary) return [];
      return summary.split(/\n\n|\n/).filter(p => p.trim().length > 0);
    },

    isSubstantiveSummary(text) {
      const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
      if (cleaned.length < 48) return false;

      const lower = cleaned.toLowerCase();
      const nameStubs = [
        this.requesterLabel,
        this.insuredLabel,
        this.project?.name,
        this.checkDefinition?.name,
      ]
        .filter(Boolean)
        .map((s) => String(s).replace(/\s+/g, ' ').trim().toLowerCase())
        .filter((s) => s.length >= 3);

      if (nameStubs.some((s) => lower === s)) return false;
      return true;
    },
  }
};
</script>

<style scoped>
.agent-upload-page {
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
}

.agent-upload-inner {
  max-width: 40rem;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

.page-header {
  margin-bottom: 1.25rem;
}

.eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.page-header h1 {
  margin: 0 0 0.45rem;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}

.purpose-lead {
  margin: 0 0 0.4rem;
  font-size: 0.98rem;
  color: #334155;
  line-height: 1.5;
  max-width: 38rem;
}

.page-meta {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
}

.purpose-panel,
.meta-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  margin-bottom: 0.85rem;
}

.purpose-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.purpose-panel--alert .purpose-icon {
  background: #fff7ed;
  color: #c2410c;
}

.purpose-body {
  min-width: 0;
}

.purpose-body h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.purpose-body p {
  margin: 0 0 0.45rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.45;
}

.purpose-body p:last-child {
  margin-bottom: 0;
}

.headline {
  color: #0f172a !important;
  font-weight: 600;
}

.next-action {
  color: #334155 !important;
}

.next-label {
  font-weight: 700;
  color: #0f172a;
}

.gap-summary {
  margin: 0.55rem 0 0;
  padding: 0.75rem 0.85rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.88rem;
  color: #334155;
  white-space: pre-wrap;
  line-height: 1.45;
}

.meta-row {
  display: grid;
  grid-template-columns: 8.5rem 1fr;
  gap: 0.75rem;
  font-size: 0.9rem;
}

.meta-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  padding-top: 0.15rem;
}

.meta-value {
  color: #0f172a;
  font-weight: 600;
}

.meta-sub {
  margin-top: 0.2rem;
  font-weight: 400;
  color: #475569;
  white-space: pre-line;
}

.sample-list {
  list-style: none;
  margin: 0.65rem 0 0;
  padding: 0;
}

.sample-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-top: 1px solid #e2e8f0;
}

.sample-item:first-child {
  border-top: none;
  padding-top: 0;
}

.sample-item-details {
  flex: 1;
  min-width: 0;
}

.sample-item-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 0.9rem;
}

.sample-description {
  font-size: 0.82rem;
  color: #64748b;
}

.sample-download {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-accent, #1e3a5f);
  text-decoration: none;
  white-space: nowrap;
}

.sample-download:hover {
  text-decoration: underline;
}

.upload-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px dashed #cbd5e1;
  border-radius: 0.75rem;
  background: #fff;
  padding: 1.5rem 1rem;
  margin-bottom: 0.85rem;
  transition: border-color 0.15s ease, background 0.15s ease;
  user-select: none;
}

.upload-drop:hover,
.upload-drop.is-drag-over {
  border-color: var(--p-accent, #1e3a5f);
  background: #f8fafc;
}

.upload-drop:focus-visible {
  outline: 2px solid var(--p-accent, #1e3a5f);
  outline-offset: 2px;
}

.home-upload-input {
  display: none;
}

.upload-drop-inner {
  text-align: center;
  max-width: 22rem;
}

.upload-drop-inner i {
  font-size: 1.75rem;
  color: var(--p-accent, #1e3a5f);
  margin-bottom: 0.55rem;
  display: block;
}

.upload-drop-inner h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.upload-purpose {
  margin: 0 0 0.55rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.4;
}

.upload-purpose strong {
  color: #0f172a;
  font-weight: 600;
}

.upload-hint {
  margin: 0;
  font-size: 0.82rem;
  color: #64748b;
}

.remove-file {
  margin-top: 0.35rem;
  background: none;
  border: none;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.upload-actions {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.7rem 1rem;
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.upload-progress p {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: #64748b;
  text-align: center;
}

.upload-progress-bar {
  height: 6px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: var(--p-accent, #1e3a5f);
  transition: width 0.2s ease;
}

.error-text,
.error-panel {
  color: #b91c1c;
  font-size: 0.9rem;
}

.error-panel {
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
}

.error-panel h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: #991b1b;
}

@media (max-width: 640px) {
  .meta-row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }

  .sample-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
